import { z } from 'zod';

import { AUTHOR_TYPES } from '@@containers/metadata/components/Author/constants';
import { DEFAULT_TEASER_ID, VARIANTS_KEY } from '@@containers/Teaser/constants';

import {
    ContentLocale,
    ContentStatus,
    Creator,
    Entity,
    ImageWithoutUrl,
    PublicationStatus,
    RichText,
    UnityRichText,
} from '../../utils/schemas/schemas';
import { unityVariantsShape, variantsShape } from '../../utils/schemas/utils';

export const EDITORIAL_ORIGIN = {
    AGENCY: 'agency',
    MANTEL: 'mantel',
    MANTEL_REACH: 'mantel_reach',
    REACH: 'reach',
    REGIONAL: 'regional',
    REGIONAL_REACH: 'regional_reach',
} as const;

const EditorialOrigin = z.nativeEnum(EDITORIAL_ORIGIN);

export type EditorialOrigin = z.infer<typeof EditorialOrigin>;

const AdditionalSettings = z.object({
    premium: z.boolean(),
    breakingNews: z.boolean(),
    live: z.boolean(),
    contest: z.boolean(),
    podcast: z.boolean(),
    renderedInWebView: z.boolean(),
    opinion: z.boolean(),
    adsEnabled: z.boolean(),
    newsletterSubscribingEnabled: z.boolean(),
    ratingEnabled: z.boolean(),
    feedbackEnabled: z.boolean(),
    sharingEnabled: z.boolean(),
    likesEnabled: z.boolean(),
    commentingEnabled: z.boolean(),
    videoInContent: z.boolean(),
    commentRisk: z.union([z.literal('low'), z.literal('high')]).optional(),
    allowRobots: z.boolean(),
    publicationDateExposed: z.boolean(),
    visibleInNewestContent: z.boolean(),
    authorBiographyDisplayed: z.boolean(),
    headDisabled: z.boolean(),
    readAloudEnabled: z.boolean(),
    aiGenerated: z.boolean(),
    opinionType: z.string().nullable(),
    syndication: z.boolean(),
    syndicationChangeDisabled: z.boolean(),
    translation: z.boolean(),
    translationChangeDisabled: z.boolean(),
});

const AuthorBase = z.object({
    id: z.number().optional(),
    manualType: z.boolean().optional(),
    info: z.string().nullish(),
    name: z.string().nullish(),
});

const UserAuthor = AuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.USER),
    user: z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
    }),
});

const ContributorAuthor = AuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.CONTRIBUTOR),
    user: z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
    }),
});

const ServiceUserAuthor = AuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.SERVICE_USER),
    user: z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
    }),
});

const AgencyAuthor = AuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.AGENCY),
    agency: z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
    }),
});

const PhotographerAuthor = AuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.PHOTOGRAPHER),
    user: z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
    }),
});

const Author = z.union([
    UserAuthor,
    ContributorAuthor,
    ServiceUserAuthor,
    AgencyAuthor,
    PhotographerAuthor,
]);

export type Author = z.infer<typeof Author>;

const UnityAuthorBase = z.object({
    id: z.number().optional(),
    info: z.string().nullish(),
    name: z.string().nullable(),
});

const UnityUserAuthor = UnityAuthorBase.extend({
    type: z.union([z.literal(AUTHOR_TYPES.USER), z.literal(AUTHOR_TYPES.MANUAL_USER)]),
    userId: z.string().nullish(),
});

const UnityContributorAuthor = UnityAuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.CONTRIBUTOR),
    userId: z.string().nullish(),
});

const UnityServiceUserAuthor = UnityAuthorBase.extend({
    type: z.literal(AUTHOR_TYPES.SERVICE_USER),
    userId: z.string().nullish(),
});

const UnityAgencyAuthor = UnityAuthorBase.extend({
    type: z.union([z.literal(AUTHOR_TYPES.AGENCY), z.literal(AUTHOR_TYPES.MANUAL_AGENCY)]),
    agencyId: z.string().nullish(),
});

const UnityPhotographerAuthor = UnityAuthorBase.extend({
    type: z.union([
        z.literal(AUTHOR_TYPES.PHOTOGRAPHER),
        z.literal(AUTHOR_TYPES.MANUAL_PHOTOGRAPHER),
    ]),
    userId: z.string().nullish(),
});

const UnityAuthor = z.union([
    UnityUserAuthor,
    UnityContributorAuthor,
    UnityServiceUserAuthor,
    UnityAgencyAuthor,
    UnityPhotographerAuthor,
]);

export type UnityAuthor = z.infer<typeof UnityAuthor>;

const SeoTeaser = z.object({
    allowAutomaticUpdate: z.boolean().optional(),
    lead: z.string().nullish(),
    title: z.string().nullish(),
});

export type SeoTeaser = z.infer<typeof SeoTeaser>;

const Teaser = z.object({
    title: z.string().nullish(),
    lead: z.string().nullish(),
    image: ImageWithoutUrl,
    tenantIds: z.array(z.number()).optional(),
    allowAutomaticUpdate: z.boolean().optional(),
});
export type Teaser = z.infer<typeof Teaser>;

const DefaultTeaser = Teaser.extend({
    titleHeader: z.string().nullish(),
    useEmbedUrl: z.boolean().optional(),
    embedUrl: z.string().nullish(),
});
export type DefaultTeaser = z.infer<typeof DefaultTeaser>;

const SocialTeaser = Teaser.extend({
    titleHeader: z.string().nullish(),
});
export type SocialTeaser = z.infer<typeof SocialTeaser>;

const SocialTeasers = z.object({
    facebook: SocialTeaser,
    twitter: SocialTeaser,
});

const OpengraphTeaser = Teaser;
export type OpengraphTeaser = z.infer<typeof OpengraphTeaser>;

const OpengraphTeasers = z.object({
    opengraph: OpengraphTeaser.optional(),
});

const AnyTeaser = z.union([DefaultTeaser, SocialTeaser, OpengraphTeaser]);
export type AnyTeaser = z.infer<typeof AnyTeaser>;

const BaseTeaserVariants = z.object({
    [DEFAULT_TEASER_ID]: DefaultTeaser,
});

/**
 * @deprecated We keep this for backward compatibility with 20min related contents
 */
const TeaserVariantWithSocial = BaseTeaserVariants.extend(SocialTeasers.shape);
const TeaserVariantWithOpengraph = BaseTeaserVariants.extend(OpengraphTeasers.shape);

const TeaserVariants = z.union([TeaserVariantWithSocial, TeaserVariantWithOpengraph]);
export type TeaserVariants = z.infer<typeof TeaserVariants>;

const Teasers = z.object({
    [VARIANTS_KEY]: TeaserVariants,
});

export type Teasers = z.infer<typeof Teasers>;

const MetadataTeaser = Teasers.extend({
    allowAutomaticUpdate: z.boolean().optional(),
});

const ScheduledPlacing = z.object({
    status: z.boolean(),
    publicationTime: z.string().nullable(),
    expiryTime: z.string().nullable(),
});

export const AB_TEST_STATUS = {
    INITIALIZED: 'initialized',
    RUNNING: 'running',
    STOPPED: 'stopped',
    COMPLETED: 'completed',
} as const;

const AbTestResult = z.object({
    pageImpressions: z.number(),
});

const AbTestResults = z.object({
    a: AbTestResult,
    b: AbTestResult,
});

export const AbTestTeasers = z.object({
    a: Teaser.extend({
        titleHeader: z.string().nullish(),
    }),
    b: Teaser.extend({
        titleHeader: z.string().nullish(),
    }),
});

export const AbTest = z.object({
    teasers: AbTestTeasers,

    /* @deprecated */
    status: z
        .union([
            z.literal(AB_TEST_STATUS.COMPLETED),
            z.literal(AB_TEST_STATUS.INITIALIZED),
            z.literal(AB_TEST_STATUS.RUNNING),
            z.literal(AB_TEST_STATUS.STOPPED),
            z.literal('legacy'),
        ])
        .nullish(),

    /* @deprecated */
    startTime: z.string().nullish(),

    /* @deprecated */
    endTime: z.string().nullish(),

    /* @deprecated */
    results: AbTestResults.nullish(),
});

export type AbTest = z.infer<typeof AbTest>;

export type ScheduledPlacing = z.infer<typeof ScheduledPlacing>;

const MetadataPlacement = z.object({
    categories: z.array(z.number()),
    categoryPlacementDetails: z
        .record(
            z.string(),
            z.object({
                position: z.number().optional(),
            }),
        )
        .optional(),
    mainCategory: z.number().nullable(),
});

const UrlSlug = z.object({
    suggested: z.string(),
    manual: z.string().nullish(),
});

export type UrlSlug = z.infer<typeof UrlSlug>;

const InfoboxBase = z.object({
    id: z.number().optional(),
    title: z.string(),
});

const UnityInfobox = InfoboxBase.extend({
    content: UnityRichText,
});

type UnityInfobox = z.infer<typeof UnityInfobox>;

const Infobox = InfoboxBase.extend({
    content: RichText,
});

export type Infobox = z.infer<typeof Infobox>;

export const PrintMetadata = z.object({
    metadataId: z.number(),
    publicationTargetDate: z.string().nullable(),
    pageName: z.string().nullable(),
    category: z.string().nullable(),
    publication: z.string().nullable(),
    type: z.string().nullable(),
    studioStatus: z.string().nullable(),
    studioDossierId: z.string().nullable(),
    desknetSyncEnabled: z.boolean().nullable(),
    desknetElementId: z.string().nullable(),
    studioSyncEnabled: z.boolean().nullable(),
});

const KickwordVariant = z.object({
    backgroundColorCode: z.string().nullable(),
    borderColorCode: z.string().nullable(),
    icon: z.any(),
    textColorCode: z.string().nullable(),
});

const KickwordBase = Entity.extend({
    id: z.number().optional(),
    name: z.string().optional(),
});

export const UnityKickword = KickwordBase.extend({
    variants: unityVariantsShape(KickwordVariant),
});

export type UnityKickword = z.infer<typeof UnityKickword>;

export const Kickword = KickwordBase.extend({
    variants: variantsShape(KickwordVariant),
});

export type Kickword = z.infer<typeof Kickword>;

export const NewKickword = Kickword.omit({ id: true });
export type NewKickword = z.infer<typeof NewKickword>;

export const NewUnityKickword = UnityKickword.omit({ id: true });
export type NewUnityKickword = z.infer<typeof NewUnityKickword>;

export const UserNeedNeed = z.union([
    z.literal('facts'),
    z.literal('depth'),
    z.literal('service'),
    z.literal('inspiration'),
    z.literal('distract'),
]);

export type UserNeedNeed = z.infer<typeof UserNeedNeed>;

const UserNeedMetadata = z.object({
    interactionId: z.string(),
    justification: z.string().nullable(),
    justificationFrench: z.string().nullable(),
    justificationGerman: z.string().nullable(),
    need: UserNeedNeed,
});
export type UserNeedMetadata = z.infer<typeof UserNeedMetadata>;

const MetadataKickword = z.object({ id: z.number().nullable() });

const HrefLangLocale = z.enum(['de-ch', 'fr-ch', 'de-de', 'fr-be', 'fr-fr']);

export const HREF_LANG_LOCALES = HrefLangLocale.options;

const HrefLangAlternative = z.object({
    locale: HrefLangLocale,
    url: z.string(),
});

export type HrefLangAlternative = z.infer<typeof HrefLangAlternative>;

const MetadataBase = Entity.extend({
    abTest: AbTest.nullable(),
    actualTextLength: z.number().nullish(),
    id: z.number(),
    contentOwner: z.string().nullish(),
    contentStatus: ContentStatus.nullable(),
    creator: Creator.nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    customPublishedAt: z.string().nullable(),
    customUpdatedAt: z.string().nullable(),
    emailAlertEnabled: z.boolean().optional(),
    pollSettings: z.object({ active: z.boolean() }).optional(),
    externalId: z.string().nullish(),
    externalSystem: z.string().nullable(),
    editorialOrigin: EditorialOrigin.nullable(),
    hreflang: z.array(HrefLangAlternative).optional(),
    lastUpdatedBy: Creator.nullish(),
    legacyCanonicalUrls: z.record(z.string(), z.unknown()).optional(),
    legacyExportIds: z.record(z.string(), z.unknown()).optional(),
    legacyIds: z.record(z.string(), z.unknown()).optional(),
    locale: ContentLocale.nullable(),
    mainTenantId: z.number().nullish(),
    publicationStatus: PublicationStatus.nullable(),
    publishedAt: z.string().nullish(),
    previewUrls: z.record(z.string(), z.string()).optional(),
    readyToPublishAt: z.string().nullish(),
    resourceId: z.string(),
    resourceName: z.string().optional(),
    scheduledPlacing: ScheduledPlacing.nullable(),
    sourceMetadataId: z.number().nullable(),
    tags: z.array(z.number()).optional(),
    targetDateTime: z.string().nullable(),
    tenantIds: z.array(z.number()),
    printMetadata: PrintMetadata.nullish(),
    targetTextLength: z.number().nullish(),
    targetUserNeed: UserNeedNeed.nullish(),
    userNeeds: z.array(UserNeedMetadata).nullable(),
    workflowChecklist: z.object({
        spellcheckingDone: z.boolean(),
        seoDone: z.boolean(),
        photoeditingDone: z.boolean(),
        proofreadingDone: z.boolean(),
        productionDone: z.boolean(),
    }),
});

export const Metadata = MetadataBase.extend({
    additionalSettings: variantsShape(AdditionalSettings).optional(),
    authors: z.array(Author),
    infobox: variantsShape({ infoboxes: z.array(Infobox) }).optional(),
    kickword: variantsShape({ kickword: MetadataKickword.nullable() }).optional(),
    note: RichText.optional(),
    placement: variantsShape(MetadataPlacement).optional(),
    seoTeasers: variantsShape(SeoTeaser),
    styles: variantsShape({ style: z.string() }).optional(),
    targetDate: z.string().nullable(),
    targetTime: z.string().nullable(),
    teasers: variantsShape(MetadataTeaser),
    urlSlugs: variantsShape(UrlSlug).optional(),
});

export type Metadata = z.infer<typeof Metadata>;

export const UnityMetadata = MetadataBase.extend({
    additionalSettings: unityVariantsShape(AdditionalSettings).nullish(),
    authors: z.array(UnityAuthor).nullable(),
    infobox: unityVariantsShape(z.array(UnityInfobox)).optional(),
    kickword: z.record(z.string(), z.union([MetadataKickword, z.null()])).optional(),
    note: UnityRichText.optional(),
    placement: unityVariantsShape(MetadataPlacement).nullish(),
    seoTeasers: z.union([unityVariantsShape(SeoTeaser), z.object({}).strict()]),
    styles: unityVariantsShape(z.string()).optional(),
    teasers: unityVariantsShape(MetadataTeaser).optional(),
    urlSlugs: unityVariantsShape(UrlSlug).optional(),
});

export type UnityMetadata = z.infer<typeof UnityMetadata>;

export const CreationMetadata = Metadata.pick({
    tenantIds: true,
}).extend({
    mainCategoryId: z.number().nullish(),
});
export type CreationMetadata = z.infer<typeof CreationMetadata>;

export const FilerepoFile = Entity.extend({
    id: z.string(),
    title: z.string().nullish(),
    // TODO: CD2-9370: Should be
    // mediaType: z.nativeEnum(IMAGE_MIME_TYPES),
    // from import IMAGE_MIME_TYPES from '@@constants/ImageMimeTypes';
    // this is a temporary workaround until we fix wrong existing filemetadata
    mediaType: z.string().nullable(),
    _links: z.object({
        self: z.object({
            href: z.string(),
        }),
        data: z.object({
            href: z.string(),
        }),
    }),
    metadata: z.object({
        name: z.string().nullish(),
        caption: z.string().nullish(),
        credit: z.string().nullish(),
        size: z.string().nullish(),
        weight: z.string().optional(),
        filename: z.string().optional(),
    }),
    supportsCropmarks: z.boolean().optional(),
    height: z.number().nullish(),
    width: z.number().nullish(),
    originalFile: z.string().optional(),
});

export type FilerepoFile = z.infer<typeof FilerepoFile>;

export const GeneeaEntityLink = z.object({
    id: z.number(),
    name: z.string(),
    type: z.string().nullish(),
});
export type GeneeaEntityLink = z.infer<typeof GeneeaEntityLink>;

export const Tag = Entity.extend({
    id: z.number(),
    active: z.boolean().optional(),
    allowRobots: z.boolean(),
    description: z.string().nullable(),
    image: z.any(),
    name: z.string(),
    title: z.string().nullable(),
    geneeaEntity: GeneeaEntityLink.nullish(),
});

export type Tag = z.infer<typeof Tag>;

export const NewTag = Tag.omit({ id: true });
export type NewTag = z.infer<typeof NewTag>;

const GeneeaEntityTagLink = z.object({
    id: z.number(),
    name: z.string(),
});
type GeneeaEntityTagLink = z.infer<typeof GeneeaEntityTagLink>;

export const GeneeaEntity = Entity.extend({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    totalCount: z.number(),
    tag: GeneeaEntityTagLink.nullable(),
});
export type GeneeaEntity = z.infer<typeof GeneeaEntity>;

export const LinkGeneeaEntityTag = z.object({
    tagId: z.number().nullable().optional(),
    name: z.string().optional(),
});
export type LinkGeneeaEntityTag = z.infer<typeof LinkGeneeaEntityTag>;

const SeoResultBase = z.object({
    status: z.enum(['success', 'error']),
    points: z.number(),
    checkId: z.string(),
    elementIds: z.array(z.string().optional()),
});

const UnitySeoResult = SeoResultBase.extend({
    text: UnityRichText,
});

const SeoResults = SeoResultBase.extend({
    text: RichText,
});

const SeoCheckGroupBase = z.object({
    groupId: z.string(),
    points: z.number(),
    maxPoints: z.number(),
});
export const UnitySeoCheckGroup = SeoCheckGroupBase.extend({
    results: z.array(UnitySeoResult),
});
export type UnitySeoCheckGroup = z.infer<typeof UnitySeoCheckGroup>;
export const SeoCheckGroup = SeoCheckGroupBase.extend({
    results: z.array(SeoResults),
});

export type SeoCheckGroup = z.infer<typeof SeoCheckGroup>;

const SeoCheckBase = Entity.extend({
    id: z.string(),
    points: z.number(),
    maxPoints: z.number(),
    keywords: z.array(z.string()),
});
export const UnitySeoCheck = SeoCheckBase.extend({
    checks: z.array(UnitySeoCheckGroup),
});
export type UnitySeoCheck = z.infer<typeof UnitySeoCheck>;

export const SeoCheck = SeoCheckBase.extend({
    checks: z.array(SeoCheckGroup),
});
export type SeoCheck = z.infer<typeof SeoCheck>;

export const TargetTextLengthOption = z.object({
    label: z.union([
        z.literal('XS'),
        z.literal('S'),
        z.literal('M'),
        z.literal('L'),
        z.literal('XL'),
        z.literal('XXL'),
        z.literal('XXXL'),
        z.literal('Special Length'),
    ]),
    validFrom: z.number(),
    specialLength: z.boolean(),
    targetLength: z.number(),
    validTo: z.number(),
});

export type TargetTextLengthOption = z.infer<typeof TargetTextLengthOption>;

export const TargetTextLengthOptions = z.object({
    options: z.array(TargetTextLengthOption),
});

export type TargetTextLengthOptions = z.infer<typeof TargetTextLengthOptions>;
